import { useId, type JSX, type ReactNode } from "react";

export type ModalFrameProps = {
  /** 親 selector 用 data-testid。 既存 PlayerScreen modal 4 種が依存している。 */
  testId: string;
  /** ヘッダーに表示するタイトル文言。 */
  title: string;
  /** close button クリックで呼ばれるコールバック。 */
  onClose: () => void;
  /** モーダル本体の中身。 */
  children: ReactNode;
};

/**
 * PlayerScreen の 4 modal (バックスクロール / 世界線ツリー / 世界線の足跡 /
 * あなたの物語の派生) で重複していた overlay + panel chrome を集約した primitive。
 * reader-style-preset-light-theme-visual の semantic token を継承する。
 *
 * a11y: role="dialog" + aria-modal + aria-labelledby (= title 自動紐付け) を提供。
 * useId で生成した安定 id を h2 に付け、 SR にタイトルを読み上げさせる。
 */
export function ModalFrame({
  testId,
  title,
  onClose,
  children,
}: ModalFrameProps): JSX.Element {
  const titleId = useId();
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid={testId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-reader-overlay overflow-y-auto p-4"
    >
      <div className="relative w-full max-w-2xl bg-reader-page rounded-lg shadow-xl mt-8">
        <div className="flex items-center justify-between p-4 border-b border-reader-border">
          <h2 id={titleId} className="text-lg font-semibold text-reader-primary">{title}</h2>
          <button
            type="button"
            aria-label="閉じる"
            onClick={onClose}
            className="inline-flex h-11 w-11 items-center justify-center text-reader-muted hover:text-reader-primary text-xl leading-none"
          >
            ✕
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
